import type { Session, SessionTrackerOptions } from '../core';

import { createSessionManager } from '../core';

// 5 minutes
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// 30 minutes
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

interface UseSessionManagerOptions<T, S = any> extends SessionTrackerOptions {
	/** How often in ms to check if the session timed out. Default: 5 minutes. */
	checkIntervalMs?: number;

	/** Duration of a session in ms. Default: 30 minutes. */
	sessionTimeoutMs?: number;

	/** Callback that runs when auto ending a session. */
	onSessionTimeout?: (previousSession: Session<T, S> | null) => void;
}

export function useSessionManager<T, S = any>(options: UseSessionManagerOptions<T, S>) {
	const manager = createSessionManager<T, S>(options);
	let intervalId: NodeJS.Timeout | null = null;

	function startInterval() {
		// already running, no-op
		if (intervalId != null) return;

		onNuxtReady(() => {
			intervalId = setInterval(() => {
				const currentSession = manager.session();
				if (currentSession == null) return;

				const now = Date.now();
				if (now - currentSession.createdAt > (options?.sessionTimeoutMs ?? SESSION_TIMEOUT_MS)) {
					const previousSession = manager.killSession();
					options?.onSessionTimeout?.(previousSession);
				}
			}, options?.checkIntervalMs ?? CHECK_INTERVAL_MS);
		});
	}

	function stopInterval() {
		if (intervalId == null) return;
		clearInterval(intervalId);
		intervalId = null;
	}

	return {
		...manager,
		startInterval,
		stopInterval,
	};
}
